import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  judgeTuningApi,
  type CreateJudgeTuningRunInput,
  type JudgeTuningRun,
  type JudgeTuningRunDetail,
  type JudgeTuningRunListItem,
  type SubmitCorrectionInput,
  type UpdateJudgeTuningRunInput,
} from '@/lib/api/judge-tuning.api';

const QK = ['judge-tuning'];

export function useJudgeTuningRunList() {
  return useQuery<JudgeTuningRunListItem[]>({ queryKey: QK, queryFn: judgeTuningApi.list });
}

export function useJudgeTuningRunDetail(id: string | undefined) {
  return useQuery<JudgeTuningRunDetail>({
    queryKey: [...QK, id],
    queryFn: () => judgeTuningApi.getById(id!),
    enabled: !!id,
    // Poll while a run is in flight so iterations stream into the UI.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'QUEUED' || status === 'RUNNING' ? 3000 : false;
    },
  });
}

export function useCreateJudgeTuningRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateJudgeTuningRunInput) => judgeTuningApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useUpdateJudgeTuningRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateJudgeTuningRunInput }) =>
      judgeTuningApi.update(id, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: [...QK, id] });
    },
  });
}

export function useDeleteJudgeTuningRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => judgeTuningApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useRunJudgeTuningRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => judgeTuningApi.run(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: [...QK, id] });
    },
  });
}

export function useApplyJudgeTuningRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, configId }: { id: string; configId?: string }) =>
      judgeTuningApi.apply(id, configId),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: [...QK, id] });
    },
  });
}

/** Runs that include a specific call -- for inline progress on the call page. */
export function useCallJudgeTuning(callId: string | undefined) {
  return useQuery<JudgeTuningRun[]>({
    queryKey: [...QK, 'by-call', callId],
    queryFn: () => judgeTuningApi.getRunsForCall(callId!),
    enabled: !!callId,
    // Poll while any run is in flight.
    refetchInterval: (query) => {
      const runs = query.state.data;
      const hasActive = runs?.some((r) => r.status === 'QUEUED' || r.status === 'RUNNING');
      return hasActive ? 3000 : false;
    },
  });
}

/** Submit a correction from the call page (upsert registry + optional tune). */
export function useSubmitCorrection(callId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitCorrectionInput) => judgeTuningApi.submitCorrection(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      if (callId) {
        qc.invalidateQueries({ queryKey: [...QK, 'by-call', callId] });
      }
    },
  });
}
